import { isAncestorPath, NavSectionLabel, navSelected } from '@repo/ui'
import React, { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'

import {
    SIDEBAR_MINIMIZED_WIDTH,
    SIDEBAR_ROW_BUTTON_INSET,
    SIDEBAR_ROW_CONTENT_LEFT,
    SIDEBAR_ROW_GUTTER,
    SIDEBAR_ROW_HEIGHT,
    SIDEBAR_ROW_INSET,
    SIDEBAR_SECTION_HEIGHT,
    SIDEBAR_SECTION_RULE_GAP,
    SIDEBAR_WIDTH,
} from 'config';
import { ROUTES } from 'routes';
// Direct subpath, not the `components/display` barrel — that barrel pulls in Table, which
// reaches back into navigation and cycles.
import { ProgressPill } from 'components/display/ProgressPill/ProgressPill';
import { isDocsPath } from 'docs/surface';

import { 
    Box,
    Tooltip,
    Stack, 
    Typography,
    Collapse,
    Divider,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
} from '@mui/material'
import { useTheme } from '@mui/material/styles';

interface SidebarListProps {
  title?: string
  links: linksType
  withBorder?: boolean
  highlight?: boolean
  sidebarOpen?: boolean
  /**
   * Show a tooltip with the row's label on hover.
   *
   * TRUE only when the sidebar is PERMANENTLY collapsed (`displayMode === 'collapsed'`), never in
   * `auto`. In auto the sidebar expands on hover and reveals the label itself, so a tooltip would
   * fire at the same moment the label appears and duplicate it. In `expanded` the label is always
   * visible. So permanently-collapsed is the only state where the icon is the whole affordance and
   * the name is otherwise unobtainable.
   */
  showTooltips?: boolean
}

export type linksType = {
  title: string
  id?: string
  link?: string
  external?: string
  /**
   * Called on click, BY REFERENCE (VRG-655).
   *
   * It used to be typed `() => {}` and invoked DURING RENDER
   * (`onClick={item.onClick !== undefined ? item.onClick() : ...}`), so any row that passed
   * one would have run it on every render: a modal-opening row would have opened its modal
   * on page load. Nothing passed one until "Refer a brand", which is why it never showed.
   *
   * A row with `onClick` and NO `link` is an ACTION row: it renders as a real `<button>`
   * (no router `<Link>`, no navigation, never "selected"), keeping the tooltip, sizing and
   * nav-icon motion of every other row.
   */
  onClick?: () => void
  count?: string
  iconId?: string
  icon?: React.ReactNode
  visible?: boolean
  /**
   * This row STARTS a group, labelled with this string (VRG-401).
   *
   * The boundary is drawn ABOVE the row, matching `ActionDropdownItem.divider` — which
   * renders its separator before the item — so the app's two menu systems cannot disagree
   * about which side of a row its boundary belongs on.
   *
   * ## ONE boundary: a rule, always — with the heading occupying its left end when there is
   * room for one
   *
   * Collapsed (80px), the boundary is the rule alone: there is nowhere to put an 11px
   * uppercase label in an 80px rail, and a truncated or wrapped one is worse than none.
   *
   * Expanded, it is `ACCOUNTS ──────────` — the SAME rule, on the same centreline, with the
   * heading taking its left end and the line running on to the rail's edge.
   *
   * An earlier pass dropped the rule entirely when expanded, on the reasoning that a heading
   * IS a group boundary and a hairline beside one says the same thing twice. That was
   * reconsidered on sight, and the version that keeps it wins for a reason the reasoning
   * above misses: it makes the two states OBVIOUSLY THE SAME OBJECT. Toggling the rail no
   * longer swaps one marker for another, it retracts the label off the left end of a line
   * that never moves. That also removes the last thing that could differ between the states —
   * and the alignment requirement this boundary is under is precisely that nothing differ.
   *
   * A duplicated marker is a real cost, but a small one here: the rule is `theme.palette
   * .divider`, ~11-12% against the rail, and it is one line at three boundaries in a 288px
   * rail rather than a rule per row.
   *
   * EVERYTHING IN THE SLOT IS CENTRED, and that one decision is what makes the heading sit
   * exactly where the collapsed rule sits AND centred between its own neighbours. Neither is
   * tuned against the other; both fall out of centring in a fixed-height box. See
   * `SIDEBAR_SECTION_HEIGHT`, which carries the arithmetic and the measured gaps.
   *
   * The slot is `SIDEBAR_SECTION_HEIGHT` tall in BOTH states. Before that existed, the
   * heading (~27px) and the rule (~9px) had genuinely different heights, so every row below a
   * boundary jumped when the sidebar toggled — measured 18-19px per boundary, enough to
   * visibly relocate the lower half of the rail.
   *
   * ## The FIRST boundary draws its rule too
   *
   * It used to be suppressed, on the grounds that a rule at the top of a list separates the
   * list from nothing. That was wrong about what is above it: the Search row is, and it is a
   * 48px pill of exactly the same shape as the rows below. So the first group was the one
   * group with no top edge — visible in the collapsed rail, where Search ran straight into
   * Signups while every later group was fenced off.
   *
   * With the rule drawn, its distance from Search's painted edge is the same 22px every other
   * boundary sits at — see the counter-margin on the nav scroll container in `Sidebar.tsx`,
   * which gives back the one pixel that container's reserved hairline border was taking.
   */
  section?: string
}[]

/**
 * A link row's button sits inside a router `<Link>`; an action row's button IS the target
 * (a `<button>`), so wrapping it in an anchor would nest two interactive elements and make
 * the row navigate.
 */
const renderRowTarget = (
  item: linksType[number],
  isAction: boolean,
  button: React.ReactElement,
): React.ReactElement =>
  isAction ? (
    button
  ) : (
    <Link id={item.id} onClick={item.onClick} to={item.link!}>
      {button}
    </Link>
  )

export const SidebarList = ({ title, links, sidebarOpen, showTooltips = false, highlight = true }: SidebarListProps) => {
  const theme = useTheme();
  const location = useLocation()

  // Does this row's link cover the current path? (The old `isSelected` body, unchanged.)
  const matchesLocation = (link: string) => {
    // VRG-227: a docs page must never light up a CONTENT nav item, and it used to light up
    // TWO at once — `/docs/certificates/studio` (category/slug) satisfied a plain substring
    // test against BOTH `/certificates` and `/studio`, and separately against `/settings`
    // for every `settings-*` guide slug (`/docs/company-settings/settings-installation` etc).
    // That was never "two rows disagreeing about who's selected" so much as one test that was
    // too loose to answer "selected" for any single row correctly — see below. Docs' own left
    // rail (DocSectionNav) already shows where you are inside docs; the app-level nav shows
    // only "Help" as active while reading it (SidebarHelpPopover reads the same predicate).
    if (isDocsPath(location.pathname)) return false
    // VRG-338: `/` is a prefix of every path, so a substring test would light Home up on
    // every page at once. The root entry is the one link that must match exactly.
    if (link === ROUTES.HOME) return location.pathname === ROUTES.HOME
    // Segment-aware, not string-prefix — the repo has been bitten by this before (`/tags` is
    // not an ancestor of `/tags-shop`, working-conventions §42). `isAncestorPath` is the same
    // predicate `ReferrerBackLink`/`Breadcrumbs` already use for exactly this question; reused
    // here rather than a second hand-rolled `startsWith` so the two can't drift apart.
    //
    // This is also what makes "at most one row selected" hold BY CONSTRUCTION rather than by a
    // separate exclusivity check: every top-level route below is a disjoint first path segment,
    // so a given pathname can equal or descend from at most one of them once the match is
    // segment-aware. The double-highlight was entirely downstream of the loose substring test.
    return location.pathname === link || isAncestorPath(link, location.pathname)
  }

  /*
    VRG-672 — THE MOST SPECIFIC MATCHING ROW WINS, so "at most one row selected" holds
    again once two rows nest.

    The note above says exclusivity held BY CONSTRUCTION because every top-level route was
    a disjoint first segment. GTM broke that on purpose: its overview is `/internal/gtm`
    and SEO & AEO is `/internal/gtm/seo`, so on the SEO page both rows matched and both lit
    up. Rather than bending the URLs to suit the matcher (an overview at
    `/internal/gtm/channels` with `/internal/gtm` redirecting), the matcher learns the one
    thing it was missing: among the rows that match, only the LONGEST link is selected.
    For every list where no two links nest — which was all of them before this — the
    result is identical to before.
  */
  const selectedLink = links
    .filter((item) => item.visible !== false && item.link && matchesLocation(item.link))
    .reduce<string | undefined>(
      (best, item) => (!best || (item.link as string).length > best.length ? (item.link as string) : best),
      undefined,
    )
  const isSelected = (link: string) => link === selectedLink
  /*
    NO `minHeight` ON THE <List> BELOW, and that is load-bearing rather than a tidy-up.

    It was `minHeight: 64` against a one-row content height of 60 (pt 4 + a 56px row), so
    a single-row list carried 4px of slack it never asked for — and a two-row list (116px)
    carried none. That made the list's rendered height depend on how many rows it happened
    to have, which is invisible in the main nav (6-12 rows, the floor never binds) but
    decides the BOTTOM group's spacing: the gap under its last row measured 12px for a
    company user (one row, slack applies) and 8px for an admin inside a company space (two
    rows, no slack). One rule cannot hold for both compositions while a floor is silently
    padding one of them, so the floor goes and the gap is owned entirely by the paddings
    that are meant to own it.

    `pt: 1` was `links[0].link == ROUTES.SETTINGS ? 1 : 1` — a ternary whose branches were
    identical, so it decided nothing while dereferencing `links[0]` unguarded. That throws
    for any list rendered empty, which BOTTOM_MENU is for a platform_manager (internal, so
    no Settings gear per VRG-395; not an admin, so no Labs).
  */
  /*
    NO `firstVisibleIndex` ANY MORE. It existed to suppress the RULE above whatever row ended
    up first — `visible: false`, or (commoner in this rail) a permission-gated entry omitted
    from the array entirely — on the reasoning that a rule at the top of a list separates the
    list from nothing. The first group draws its rule now, because what is above it is the
    Search row rather than nothing; see the `section` docblock.
  */

  return (
    <List sx={{
      pb: 0,
      pt: 1,
      overflowX: 'hidden',
    }}>
        {links.map((item) => {
            if (item.visible !== false) {
                // See the `section` docblock on linksType: ONE boundary — a rule, plus the
                // heading when the rail is wide enough for one — in a fixed-height SLOT, so
                // neither state can shift the rows below it.
                const isSectionBoundary = !!item.section
                // VRG-655 — an action row (opens something) rather than a route.
                const isAction = !item.link && !!item.onClick
                return (
                  <React.Fragment key={item.title}>
                  {isSectionBoundary && (
                    <Box
                      sx={{
                        height: SIDEBAR_SECTION_HEIGHT,
                        display: 'flex',
                        // CENTRED, in both states and for both children. This is the single
                        // line that puts the heading exactly where the collapsed rule sits
                        // AND centred between its neighbouring rows — see the `section`
                        // docblock above and SIDEBAR_SECTION_HEIGHT for the arithmetic. It
                        // was `flex-end` when expanded, which bought a deliberate 20/8
                        // asymmetry and cost 6.1px of disagreement with the rule.
                        alignItems: 'center',
                      }}
                    >
                      {sidebarOpen && (
                        // `compact`, not the settings nav's default density. Measured: at the
                        // settings spacing this rail's three headings would have cost ~118px,
                        // in a nav that is already 694px of rows in a 249px box at 1280x720.
                        // Shared component rather than a fourth copy of the same eight CSS
                        // properties — working-conventions §36.
                        //
                        //   paddingLeft  — the row's CONTENT start (the icon's left edge), not
                        //                  the component's 12px default, which lines up with the
                        //                  `<ListItem>` and therefore sat 16px left of every icon
                        //                  beneath it. See SIDEBAR_ROW_CONTENT_LEFT for why the
                        //                  icon column and not the text column.
                        //   paddingTop/Bottom — ZERO, both of them. The slot centres the line
                        //                  box, so any padding here would be padding the thing
                        //                  being centred and would move the text off the rule.
                        //                  The density's own 8/4 is overridden for that reason.
                        //
                        // `flexShrink: 0` so the rule, not the label, absorbs the remaining
                        // width — an 11px all-caps word must never be the thing that squeezes.
                        <NavSectionLabel
                          density="compact"
                          sx={{
                            paddingLeft: `${SIDEBAR_ROW_CONTENT_LEFT}px`,
                            paddingTop: 0,
                            paddingBottom: 0,
                            flexShrink: 0,
                          }}
                        >
                          {item.section}
                        </NavSectionLabel>
                      )}
                      {/*
                        THE RULE, IN BOTH STATES AND AT EVERY BOUNDARY INCLUDING THE FIRST.

                        Same token and same weight as the bottom group's own `<Divider>` a few
                        rows below — `theme.palette.divider`, which composites to ~11% on the
                        dark sidebar and ~12% on the light one. Deliberately NOT `primary[100]`:
                        that measures ~5.9% on this raised surface, i.e. half the weight, and
                        two separators a short distance apart at different weights is the
                        "reads as a rendering fault" failure documented on the nav list's
                        scroll hairlines.

                        `flex: 1` rather than `width: 100%`, so one element serves both states:
                        collapsed it is the whole rail, expanded it is whatever the heading
                        leaves. Either way it runs to the rail's RIGHT edge — the rows are
                        inset by their own `pl/pr: 3`, and a separator that stops short of the
                        edge reads as belonging to one row rather than sitting between groups.

                        No `my` — the slot owns the vertical centring, which is what puts this
                        line and the heading's line box on one centreline.
                      */}
                      <Divider
                        sx={{
                          flex: 1,
                          ...(sidebarOpen ? { ml: `${SIDEBAR_SECTION_RULE_GAP}px` } : {}),
                        }}
                      />
                    </Box>
                  )}
                  <ListItem
                    sx={{
                      // Named constants, not `pt: 1` / `pl: 3` literals. The section heading's
                      // left edge and its vertical gaps are DERIVED from these four numbers
                      // (SIDEBAR_ROW_CONTENT_LEFT, SIDEBAR_SECTION_HEIGHT), and a literal here
                      // is a number the heading cannot see change.
                      pt: `${SIDEBAR_ROW_GUTTER}px`,
                      pb: `${SIDEBAR_ROW_GUTTER}px`,
                      pr: `${SIDEBAR_ROW_INSET}px`,
                      pl: `${SIDEBAR_ROW_INSET}px`,
                      textDecoration: 'none',
                      '&.Mui-selected': {
                        background: navSelected(theme),
                      },
                    }}>
                    {/* Tooltip wraps the LINK, not the button: MUI needs a single child that can
                        hold a ref and forward hover, and the Link is the element that spans the
                        whole row. `disableHoverListener` when not collapsed leaves the DOM shape
                        identical in every mode, so nothing about the row's layout depends on
                        which mode is active. */}
                    <Tooltip
                      title={item.title}
                      placement="right"
                      enterDelay={400}
                      disableHoverListener={!showTooltips}
                      disableFocusListener={!showTooltips}
                      disableTouchListener={!showTooltips}
                    >
                    {renderRowTarget(
                      item,
                      isAction,
                      <ListItemButton
                        disableRipple={true}
                        selected={isAction ? false : isSelected(item.link as string)}
                        {...(isAction
                          ? { component: 'button' as const, type: 'button', id: item.id, onClick: item.onClick }
                          : {})}
                        sx={[
                          {
                            borderRadius: 2,
                            // FIXED height, not padding-derived.
                            //
                            // These rows were sized by `pt/pb: 3` plus whatever the tallest
                            // child happened to be, which made the row's height a property of
                            // its CONTENTS. That is fine until a row gains one. When the Home
                            // row grew its setup-progress pill — which only renders with the
                            // sidebar OPEN — the row went 48px -> 54px, and every row beneath it
                            // moved down 6px on expand and back up on collapse. Measured, not
                            // inferred: Collapse and Search (above Home) held station while
                            // Analytics through Studio all shifted by exactly 6.
                            //
                            // Pinning the height fixes the whole class rather than this one
                            // instance: a badge, a two-line label, a taller icon can no longer
                            // move the rows below it. 48 is not a new number — it is what every
                            // row already measured, and `SIDEBAR_ROW_HEIGHT` is the constant the
                            // search trigger, the collapse toggle and the list's own minimum
                            // already read. Its docstring carries a TODO to converge the
                            // remaining literals; this is the nav list doing so.
                            height: SIDEBAR_ROW_HEIGHT,
                            pt: 3,
                            pb: 3,
                            pr: 4,
                            // EXPLICIT, though it is the same 16px MUI's ListItemButton already
                            // defaulted to. The default was invisible, so the section heading's
                            // left edge had nothing to be derived from and was hand-set to 12 —
                            // 16px adrift of every icon it sat above. Stating it makes
                            // SIDEBAR_ROW_CONTENT_LEFT a real derivation rather than a guess.
                            pl: `${SIDEBAR_ROW_BUTTON_INSET}px`,
                            // navChrome, so the sidebar, the in-page sub-nav and every dropdown
                            // state the same thing. This was `primary[100]` — a SOLID grey — while
                            // menus used a brand tint, which is why the sidebar read as a
                            // different language from the menu that opens out of it.
                            // Hover is owned by the theme's MuiListItemButton override now, so
                            // it is deliberately absent here. Only the `highlight` gating is
                            // this component's business.
                            '&.Mui-selected ':{
                              background: highlight ? navSelected(theme) : 'transparent',
                              ':hover': {
                                background: navSelected(theme),
                              }
                            },
                          },
                          // A <button> is inline-level: it does not stretch the way the
                          // <Link> wrapper lets a link row's button stretch.
                          isAction ? { width: '100%', textAlign: 'left' as const } : {},
                          sidebarOpen
                            ? {
                                minWidth: SIDEBAR_WIDTH - 24,
                                justifyContent: 'initial',
                              }
                            : {
                                maxWidth: SIDEBAR_MINIMIZED_WIDTH - 24
                            }
                        ]}
                      >
                        <ListItemIcon
                          sx={[
                            {
                              // `inherit`, so the glyph moves with its row: the
                              // MuiListItemButton override rests the row at
                              // text.secondary and brightens it on hover/selection,
                              // and lucide strokes with `currentColor`. Pinned to
                              // primary[900] the label dimmed while the icon stayed
                              // at full white — measured 161,161,170 against
                              // 250,250,250 — so each row read as half-lit.
                              color: 'inherit',
                              minWidth: 0,
                              justifyContent: 'center',
                            },
                            sidebarOpen
                              ? {
                                  mr: 0,
                                }
                              : {
                                  mr: 'auto',
                                },
                          ]}
                        >
                          {item.icon}
                        </ListItemIcon>
                        <ListItemText
                          disableTypography
                          sx={[{
                            fontSize: 16,
                            fontFamily: 'Lato',
                            display: 'flex',
                            justifyContent: 'space-between',
                            width: '100%',
                            alignItems: 'center',
                            marginLeft: 3,
                          },
                            sidebarOpen
                              ? {
                                  opacity: 1,
                                  display: 'flex'
                                }
                              : {
                                  opacity: 0,
                                  display: 'none'
                                },
                          ]}
                        >
                          {item.title}
                          {/* `count` has been on `linksType` all along and nothing drew it.
                              VRG-340's Home needs it: setup progress ("2/6") has to be legible
                              from wherever you are, because the step that advances it is always
                              completed on some OTHER page. The row is already
                              `space-between` at full width, so the badge takes the right edge.

                              Only rendered with the sidebar OPEN — `ListItemText` is
                              `display:none` when collapsed, so there is nowhere to put it and a
                              floating dot over a 24px icon would be noise. The toast covers the
                              moment of change; this covers the rest of the time. */}
                          {item.count && <ProgressPill>{item.count}</ProgressPill>}
                        </ListItemText>
                      </ListItemButton>
                    )}
                    </Tooltip>
                  </ListItem>
                  </React.Fragment>
                )
            }
        })}
    </List>
  )
}
